import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const ROWS = [
    { key: 'kept', label: 'Allocation kept', hint: 'Allocated lines whose allocation still fits' },
    { key: 'auto', label: 'Updated automatically', hint: 'Single-container lines that follow the new quantity' },
    { key: 'unallocated', label: 'Moved to unallocated', hint: 'Allocation no longer fits. Allocate these lines again after confirming' },
    { key: 'deleted', label: 'Deleted lines', hint: 'Removed lines and their allocations' },
    { key: 'shipperData', label: 'Customer data removed', hint: 'Custom field values and stars on deleted lines' },
];

/**
 * 재확정 직전 영향 요약. 서버가 계산한 배분 유지·자동 반영·미배분 전환·삭제 라인 수와 함께 지워지는 화주 데이터 건수를 보여주고,
 * Confirm을 눌러 동의해야 재확정을 보낸다. 컨테이너별 수량을 나눠 줄이지 않으므로 따로 입력받을 것은 없다.
 * 서버 검증에 걸린 항목(issues)이 있으면 목록으로 보여주고 Confirm을 막는다 — 고친 뒤 다시 Confirm하면 요약을 새로 받는다.
 */
export default function ImpactSummaryDialog({ open, impact, invoiceNo, saving, onCancel, onConfirm }) {
    const issues = impact?.issues ?? [];
    return (
        <Dialog open={open} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14, color: '#003a52' }}>Confirm changes to {invoiceNo || 'this packing list'}?</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 12 }}>Review how this edit affects allocations before confirming.</Typography>
                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                    {ROWS.map((row) => (
                        <Box key={row.key} sx={{ display: 'contents' }}>
                            <Box component="dt">
                                <Typography sx={{ fontSize: 12, color: '#003a52', fontWeight: 500 }}>{row.label}</Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.hint}</Typography>
                            </Box>
                            <Box component="dd" sx={{ m: 0, alignSelf: 'center' }}>
                                <Typography data-testid={`impact-${row.key}`} sx={{ fontSize: 14, fontWeight: 600, color: row.key === 'unallocated' && impact?.[row.key] > 0 ? 'warning.main' : '#003a52' }}>
                                    {(impact?.[row.key] ?? 0).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                {impact?.added > 0 && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{impact.added} new line(s) will start unallocated.</Typography>}
                {issues.length > 0 && (
                    <Alert severity="error" sx={{ py: 0, fontSize: 12, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Fix these before confirming</Typography>
                        {issues.map((issue, index) => (
                            <Typography key={`${issue.code}-${issue.field}-${index}`} sx={{ fontSize: 11 }}>
                                {issue.message}
                            </Typography>
                        ))}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button size="small" onClick={onCancel} disabled={saving} sx={{ fontSize: 12 }}>Back to edit</Button>
                <Button size="small" variant="contained" onClick={onConfirm} disabled={saving || issues.length > 0} sx={{ fontSize: 12 }}>{saving ? 'Confirming…' : 'Confirm'}</Button>
            </DialogActions>
        </Dialog>
    );
}
